import { useDraftDirty, useUnsavedChanges } from "./components/common/UnsavedChangesBoundary";
import React, { useEffect, useId, useRef, useState } from "react";
import {
  updateAction,
  addActionNote,
  updateActionNote,
  deleteActionNote,
  addActionAttachment,
} from "./api/actions.api.js";
import { createTaxonomyValue } from "./api/taxonomy.api";
import { AuthContext } from "./AuthContext";
import ConfirmDialog from "./components/common/ConfirmDialog";
import RelatedKpisPanel from "./components/kpi/RelatedKpisPanel";
import { useToast } from "./components/common/ToastProvider";
import { hasModuleAccess, canAccessAdminModules } from "./accessControl";
import { Button, Input, Select, StatusBadge, Textarea } from "./ui";
import DestructiveConfirm from "./components/common/DestructiveConfirm";
import EvidenceAssociations from "./components/EvidenceAssociations";
import { colors, radius, spacing, typography } from "./ui/tokens";
import { toolbarStyle } from "./ui/pagePatterns";
import {
  ACTION_STATUS_OPTIONS,
  getActionDisplayStatus,
  getActionStatusLabel,
  isActionCompletionEvidenceNote,
  isCompletedActionStatus,
  normalizeActionStatus,
} from "./utils/actionStatus";
import {
  ACTION_CATEGORY_OPTIONS_CHANGED_EVENT,
  useActionCategoryOptions,
} from "./hooks/useActionCategoryOptions";
import useRegisterRecentView from "./features/recentViews/useRegisterRecentView";

const DETAIL_TABS = [
  { key: "overview", label: "Overview" },
  { key: "workflow", label: "Workflow" },
  { key: "evidence", label: "Evidence" },
  { key: "history", label: "History" },
];

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "Not set");

const formatOptionalText = (value, fallback = "Not set") => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
};

const splitLinks = (value) =>
  Array.isArray(value)
    ? value.map((item) => String(item || "").trim()).filter(Boolean)
    : String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

const getCurrentStatusValue = (action) => normalizeActionStatus(action?.status) || "OPEN";

const getStatusUpdateErrorMessage = (err) => {
  const detailItems = Array.isArray(err?.responseBody?.detail) ? err.responseBody.detail : null;
  const validationMessage = detailItems?.map((item) => item?.msg).find(Boolean);
  if (validationMessage) return validationMessage;

  const detail =
    typeof err?.detail === "string"
      ? err.detail.trim()
      : typeof err?.responseBody?.error === "string"
        ? err.responseBody.error.trim()
      : typeof err?.message === "string"
        ? err.message.trim()
        : "";
  if (detail && detail !== "Request failed" && !detail.includes("[object Object]")) {
    return detail;
  }
  return "The Action status could not be updated. Please try again.";
};

export default function ActionDetails({
  action,
  loading = false,
  history = [],
  onUpdateAction,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  onAddAttachment,
  actionsError,
  onCreateChild,
  onOpenAction,
}) {
  const auth = React.useContext(AuthContext);
  const toast = useToast();
  const tabsId = useId();
  const tabRefs = useRef({});
  const noteInputRef = useRef(null);
  useRegisterRecentView({
    itemType: "action",
    itemId: action?.id,
    title: action?.title || action?.name,
    secondaryText: action?.status || action?.category || null,
    enabled: Boolean(action?.id && (action?.title || action?.name)),
  });

  const canCreateCategories = canAccessAdminModules(auth?.user);
  const canEdit = hasModuleAccess(auth?.user, "actions", "EDIT");
  const [activeTab, setActiveTab] = useState("overview");
  const [statusDraft, setStatusDraft] = useState(getCurrentStatusValue(action));
  const [statusSaving, setStatusSaving] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState(null);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentDesc, setAttachmentDesc] = useState("");
  const [localError, setLocalError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [createdCategoryOptions, setCreatedCategoryOptions] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryError, setNewCategoryError] = useState("");
  const [focusNoteComposer, setFocusNoteComposer] = useState(false);
  const [draft, setDraft] = useState({
    title: action?.title || action?.name || "",
    description: action?.description || "",
    category: action?.category || "",
    owner: action?.owner || "",
    assignee: action?.assignee || "",
    priority: action?.priority || "",
    due_date: action?.due_date || "",
    source_ref: action?.source_ref || "",
    evidence_links: Array.isArray(action?.evidence_links) ? action.evidence_links.join(", ") : "",
  });
  const requestClose = useUnsavedChanges(useDraftDirty({ draft, newCategoryName }, editing));
  const draftActionId = useRef(action?.id);

  const categoryOptions = Array.from(
    new Set([...(createdCategoryOptions || []), ...(useActionCategoryOptions(draft.category) || [])])
  );

  useEffect(() => {
    if (editing && draftActionId.current === action?.id) return;
    draftActionId.current = action?.id;
    setActiveTab("overview");
    setStatusDraft(getCurrentStatusValue(action));
    setLocalError(null);
    setStatusSaving(false);
    setEditingNoteId(null);
    setEditingNoteText("");
    setConfirmDeleteNoteId(null);
    setEditing(false);
    setShowCreateCategory(false);
    setCreatingCategory(false);
    setCreatedCategoryOptions([]);
    setNewCategoryName("");
    setNewCategoryError("");
    setFocusNoteComposer(false);
    setDraft({
      title: action?.title || action?.name || "",
      description: action?.description || "",
      category: action?.category || "",
      owner: action?.owner || "",
      assignee: action?.assignee || "",
      priority: action?.priority || "",
      due_date: action?.due_date || "",
      source_ref: action?.source_ref || "",
      evidence_links: Array.isArray(action?.evidence_links) ? action.evidence_links.join(", ") : "",
    });
  }, [action, editing]);

  useEffect(() => {
    if (!focusNoteComposer || activeTab !== "history" || loading || !action) return;
    noteInputRef.current?.focus?.();
    setFocusNoteComposer(false);
  }, [activeTab, focusNoteComposer, loading, action]);

  if (loading) {
    return <div style={{ padding: 20 }}>Loading action...</div>;
  }

  if (!action) {
    return (
      <div style={{ padding: 8, fontStyle: "italic", color: colors.textMuted }}>
        {actionsError || "Select an action to view details."}
      </div>
    );
  }

  const actionTitle = action.title || action.name || `Action #${action.id}`;
  const displayStatus = getActionDisplayStatus(action);
  const statusDraftLabel = getActionStatusLabel(statusDraft, "Open");
  const childActions = Array.isArray(action.children) ? action.children : [];
  const notes = Array.isArray(action.notes) ? action.notes : [];
  const attachments = Array.isArray(action.attachments) ? action.attachments : [];
  const completionEvidenceNotes = notes.filter(isActionCompletionEvidenceNote);
  const historyEntries =
    Array.isArray(history) && history.length > 0
      ? history
      : Array.isArray(action.history)
        ? action.history
        : [];
  const evidenceLinks = splitLinks(action.evidence_links);
  const parentActionId = action.parent_action_id ? Number(action.parent_action_id) : null;
  const linkedRiskId = action.risk_id ? Number(action.risk_id) : null;
  const linkedControlId = action.control_id ? Number(action.control_id) : null;

  const resetDraft = () => {
    setEditing(false);
    setShowCreateCategory(false);
    setCreatingCategory(false);
    setCreatedCategoryOptions([]);
    setNewCategoryName("");
    setNewCategoryError("");
    setDraft({
      title: action?.title || action?.name || "",
      description: action?.description || "",
      category: action?.category || "",
      owner: action?.owner || "",
      assignee: action?.assignee || "",
      priority: action?.priority || "",
      due_date: action?.due_date || "",
      source_ref: action?.source_ref || "",
      evidence_links: Array.isArray(action?.evidence_links) ? action.evidence_links.join(", ") : "",
    });
  };

  const handleStatusSave = async () => {
    setLocalError(null);
    const hasOpenChildren =
      childActions.length > 0 && childActions.some((child) => !isCompletedActionStatus(child.status));

    if (statusDraft === "COMPLETED") {
      if (completionEvidenceNotes.length === 0 && attachments.length === 0) {
        setLocalError("Add at least one note or attachment before completing.");
        setActiveTab("history");
        setEditingNoteId(null);
        setEditingNoteText("");
        setFocusNoteComposer(true);
        return;
      }
      if (hasOpenChildren) {
        setLocalError("Complete all child actions before closing the parent.");
        return;
      }
    }

    setStatusSaving(true);
    try {
      if (onUpdateAction) {
        await onUpdateAction({ status: statusDraft });
      } else {
        await updateAction(action.id, { status: statusDraft });
      }
    } catch (err) {
      setLocalError(getStatusUpdateErrorMessage(err));
      setStatusDraft(getCurrentStatusValue(action));
    } finally {
      setStatusSaving(false);
    }
  };

  const requestStatusSave = () => {
    if (statusDraft === "COMPLETED") {
      setConfirmComplete(true);
      return;
    }
    void handleStatusSave();
  };

  const handleSaveDetails = async () => {
    setLocalError(null);

    const payload = {
      title: draft.title.trim() || action.title || action.name,
      description: draft.description.trim() || null,
      category: draft.category.trim() || null,
      owner: draft.owner.trim() || null,
      assignee: draft.assignee.trim() || null,
      priority: draft.priority.trim() || null,
      source_ref: draft.source_ref.trim() || null,
      evidence_links: draft.evidence_links
        ? draft.evidence_links
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        : [],
    };

    const dateValue = draft.due_date ? new Date(draft.due_date) : null;
    payload.due_date = dateValue && !Number.isNaN(dateValue.getTime()) ? dateValue.toISOString() : null;

    try {
      if (onUpdateAction) {
        await onUpdateAction(payload);
      } else {
        await updateAction(action.id, payload);
      }
      setEditing(false);
    } catch (err) {
      setLocalError(err?.message || "Failed to save action");
    }
  };

  const handleCreateCategory = async () => {
    const trimmedName = String(newCategoryName || "").trim();
    if (!trimmedName) {
      setNewCategoryError("Category name is required.");
      return;
    }

    const hasDuplicate = categoryOptions.some(
      (option) => String(option || "").trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (hasDuplicate) {
      setNewCategoryError("Category name already exists.");
      return;
    }

    setCreatingCategory(true);
    setNewCategoryError("");
    try {
      await createTaxonomyValue("action_category", {
        label: trimmedName,
        value: trimmedName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, ""),
        status: "active",
      });
      window.dispatchEvent(new CustomEvent(ACTION_CATEGORY_OPTIONS_CHANGED_EVENT));
      setCreatedCategoryOptions((current) =>
        current.includes(trimmedName) ? current : [trimmedName, ...current]
      );
      setDraft((prev) => ({ ...prev, category: trimmedName }));
      setShowCreateCategory(false);
      setNewCategoryName("");
      toast.success("Action category created.");
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.detail ||
        err?.message ||
        "Unable to create action category.";
      setNewCategoryError(message);
      toast.error(message);
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    if (onAddNote) {
      await onAddNote(noteText.trim());
    } else {
      await addActionNote(action.id, { body: noteText.trim(), author: action?.owner || null });
    }
    setNoteText("");
  };

  const handleSaveNoteEdit = async (noteId) => {
    if (!editingNoteText.trim()) return;
    if (onUpdateNote) {
      await onUpdateNote(noteId, editingNoteText.trim());
    } else {
      await updateActionNote(noteId, { body: editingNoteText.trim() });
    }
    setEditingNoteId(null);
    setEditingNoteText("");
  };

  const handleAddAttachment = async () => {
    if (!attachmentUrl.trim()) return;

    const payload = {
      url: attachmentUrl.trim(),
      description: attachmentDesc.trim() || null,
      uploaded_by: action?.owner || null,
    };

    if (onAddAttachment) {
      await onAddAttachment(payload);
    } else {
      await addActionAttachment(action.id, payload);
    }

    setAttachmentUrl("");
    setAttachmentDesc("");
  };

  const handleTabKeyDown = (event, index) => {
    const lastIndex = DETAIL_TABS.length - 1;
    let nextIndex = null;

    if (event.key === "ArrowRight") {
      nextIndex = index === lastIndex ? 0 : index + 1;
    } else if (event.key === "ArrowLeft") {
      nextIndex = index === 0 ? lastIndex : index - 1;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = lastIndex;
    }

    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = DETAIL_TABS[nextIndex];
    setActiveTab(nextTab.key);
    tabRefs.current[nextTab.key]?.focus();
  };

  return (
    <div style={pageStyle}>
      {actionsError && (
        <div role="alert" style={errorBannerStyle}>
          {actionsError}
        </div>
      )}
      {localError && (
        <div role="alert" style={errorBannerStyle}>
          {localError}
        </div>
      )}

      <div style={tabHeaderStyle}>
        <div role="tablist" aria-label="Action detail sections" style={tabListStyle}>
          {DETAIL_TABS.map((tab, index) => {
            const selected = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                ref={(element) => {
                  tabRefs.current[tab.key] = element;
                }}
                id={`${tabsId}-${tab.key}-tab`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={`${tabsId}-${tab.key}-panel`}
                tabIndex={selected ? 0 : -1}
                style={{
                  ...tabButtonStyle,
                  ...(selected ? tabButtonActiveStyle : null),
                }}
                onClick={() => setActiveTab(tab.key)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <div style={headerActionBarStyle}>
          {!editing ? (
            <Button disabled={!canEdit} onClick={() => setEditing(true)} variant="secondary">
              Edit
            </Button>
          ) : (
            <>
              <Button disabled={!canEdit} onClick={handleSaveDetails} variant="primary">
                Save
              </Button>
              <Button onClick={requestClose || resetDraft} variant="secondary">
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      <TabPanel
        tabsId={tabsId}
        tabKey="overview"
        label="Overview"
        activeTab={activeTab}
      >
        <Section title="Description">
          {editing ? (
            <div style={{ display: "grid", gap: spacing.md }}>
              <Input
                label="Title"
                value={draft.title}
                onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Title"
                style={{ width: "100%" }}
              />
              <Textarea
                label="Description"
                value={draft.description}
                onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Describe the action"
                rows={5}
                style={{ width: "100%" }}
              />
            </div>
          ) : action.description ? (
            <p style={descriptionTextStyle}>{action.description}</p>
          ) : (
            <QuietEmptyText>Description not set.</QuietEmptyText>
          )}
        </Section>

        <Section title="Summary">
          {editing ? (
            <div style={formGridStyle}>
              <Select
                label="Category"
                value={draft.category}
                onChange={(event) => setDraft((prev) => ({ ...prev, category: event.target.value }))}
                style={{ width: "100%" }}
              >
                <option value="">Select category</option>
                {categoryOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
              <Input
                label="Priority"
                value={draft.priority}
                onChange={(event) => setDraft((prev) => ({ ...prev, priority: event.target.value }))}
                placeholder="Priority"
                style={{ width: "100%" }}
              />
              <Input
                label="Owner"
                value={draft.owner}
                onChange={(event) => setDraft((prev) => ({ ...prev, owner: event.target.value }))}
                placeholder="Owner"
                style={{ width: "100%" }}
              />
              <Input
                label="Assignee"
                value={draft.assignee}
                onChange={(event) => setDraft((prev) => ({ ...prev, assignee: event.target.value }))}
                placeholder="Assignee"
                style={{ width: "100%" }}
              />
              <Input
                label="Due date"
                type="date"
                value={draft.due_date ? new Date(draft.due_date).toISOString().slice(0, 10) : ""}
                onChange={(event) => setDraft((prev) => ({ ...prev, due_date: event.target.value }))}
                style={{ width: "100%" }}
              />
              <Input
                label="Source reference"
                value={draft.source_ref}
                onChange={(event) => setDraft((prev) => ({ ...prev, source_ref: event.target.value }))}
                placeholder="Source reference"
                style={{ width: "100%" }}
              />
            </div>
          ) : (
            <dl style={summaryGridStyle}>
              <SummaryField label="Priority" value={action.priority} />
              <SummaryField label="Due date" value={action.due_date ? formatDateTime(action.due_date) : null} />
              <SummaryField label="Category" value={action.category} />
              <SummaryField label="Owner" value={action.owner} />
              <SummaryField label="Assignee" value={action.assignee} />
              <SummaryField label="Source ref" value={action.source_ref} />
              <SummaryField label="Created" value={formatDateTime(action.created_at)} />
              <SummaryField label="Last updated" value={formatDateTime(action.updated_at)} />
            </dl>
          )}
          {editing ? (
            <div style={createCategoryWrapStyle}>
              {canCreateCategories ? (
                <>
                  <div style={createCategoryHeaderStyle}>
                    <span style={inlineHelperTextStyle}>Need a new category?</span>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowCreateCategory((current) => !current);
                        setNewCategoryError("");
                      }}
                      style={inlineButtonStyle}
                    >
                      {showCreateCategory ? "Cancel new category" : "Create category"}
                    </Button>
                  </div>
                  {showCreateCategory ? (
                    <div style={createCategoryPanelStyle}>
                      <Input
                        label="New category name"
                        value={newCategoryName}
                        onChange={(event) => {
                          setNewCategoryName(event.target.value);
                          if (newCategoryError) {
                            setNewCategoryError("");
                          }
                        }}
                        placeholder="e.g., Remediation"
                        error={newCategoryError || undefined}
                        style={{ width: "100%" }}
                      />
                      <Button
                        type="button"
                        variant="primary"
                        onClick={handleCreateCategory}
                        disabled={creatingCategory}
                        style={inlineButtonStyle}
                      >
                        {creatingCategory ? "Creating..." : "Create category"}
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : (
                <span style={inlineHelperTextStyle}>
                  Ask an administrator to add a new action category if the existing list does not fit.
                </span>
              )}
            </div>
          ) : null}
        </Section>

        <Section title="Related records">
          <div style={relatedRecordsWrapStyle}>
            <RelatedRecord
              label="Parent action"
              value={parentActionId ? `Action #${parentActionId}` : null}
              onActivate={parentActionId && onOpenAction ? () => onOpenAction(parentActionId) : null}
            />
            <RelatedRecord label="Linked risk" value={linkedRiskId ? `Risk #${linkedRiskId}` : null} />
            <RelatedRecord label="Linked control" value={linkedControlId ? `Control #${linkedControlId}` : null} />
          </div>
          <div style={kpiPanelWrapStyle}>
            <div style={subsectionTitleStyle}>Related KPIs</div>
            <RelatedKpisPanel entityType="action" entityId={action.id} />
          </div>
        </Section>
      </TabPanel>

      <TabPanel
        tabsId={tabsId}
        tabKey="workflow"
        label="Workflow"
        activeTab={activeTab}
      >
        <Section title="Status">
          <div style={workflowBarStyle}>
            <Select
              label="Status"
              value={statusDraft}
              onChange={(event) => setStatusDraft(event.target.value)}
              style={{ minWidth: 180 }}
              disabled={!canEdit || statusSaving}
            >
              {ACTION_STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </Select>
            <Button
              onClick={requestStatusSave}
              variant="secondary"
              style={{ marginTop: 18 }}
              disabled={!canEdit || statusSaving}
            >
              {statusSaving ? "Updating..." : "Update Status"}
            </Button>
          </div>
          <dl style={summaryGridStyle}>
            <SummaryField
              label="Current status"
              value={<StatusBadge variant="status" value={displayStatus} fallbackLabel={displayStatus} />}
            />
            <SummaryField label="Priority" value={action.priority} />
            <SummaryField label="Due date" value={action.due_date ? formatDateTime(action.due_date) : null} />
            <SummaryField label="Child actions" value={String(childActions.length)} />
          </dl>
        </Section>

        <Section
          title="Parent action"
          actions={
            parentActionId && onOpenAction ? (
              <Button type="button" variant="secondary" onClick={() => onOpenAction(parentActionId)}>
                Open Parent Action
              </Button>
            ) : null
          }
        >
          {parentActionId ? (
            <button
              type="button"
              style={relatedActionButtonStyle}
              onClick={() => onOpenAction?.(parentActionId)}
              disabled={!onOpenAction}
            >
              <span style={relatedActionTitleStyle}>Action #{parentActionId}</span>
              <span style={relatedActionMetaStyle}>Open the parent action in this drawer.</span>
            </button>
          ) : (
            <QuietEmptyText>No parent action.</QuietEmptyText>
          )}
        </Section>

        <Section
          title="Child actions"
          actions={
            onCreateChild ? (
              <Button disabled={!canEdit} onClick={onCreateChild} variant="secondary">
                Add Child Action
              </Button>
            ) : null
          }
        >
          {childActions.length > 0 ? (
            <div style={listStackStyle}>
              {childActions.map((child) => (
                <ChildActionRow
                  key={child.id}
                  child={child}
                  onOpenAction={onOpenAction}
                />
              ))}
            </div>
          ) : (
            <QuietEmptyText>No child actions.</QuietEmptyText>
          )}
        </Section>
      </TabPanel>

      <TabPanel
        tabsId={tabsId}
        tabKey="evidence"
        label="Evidence"
        activeTab={activeTab}
      >
        <EvidenceAssociations parentType="action" parentId={action.id} writable={canEdit} />
        <Section title="Evidence links">
          {editing ? (
            <Input
              label="Evidence links"
              value={draft.evidence_links}
              onChange={(event) => setDraft((prev) => ({ ...prev, evidence_links: event.target.value }))}
              placeholder="Evidence links (comma separated)"
              style={{ width: "100%" }}
            />
          ) : evidenceLinks.length > 0 ? (
            <div style={listStackStyle}>
              {evidenceLinks.map((link) => (
                <a key={link} href={link} target="_blank" rel="noreferrer" style={linkRowStyle}>
                  {link}
                </a>
              ))}
            </div>
          ) : (
            <QuietEmptyText>No evidence added.</QuietEmptyText>
          )}
        </Section>

        <Section title="Attachments">
          <div style={formGridStyle}>
            <Input
              label="Attachment URL"
              value={attachmentUrl}
              onChange={(event) => setAttachmentUrl(event.target.value)}
              placeholder="Attachment URL"
              style={{ width: "100%" }}
            />
            <Input
              label="Attachment description"
              value={attachmentDesc}
              onChange={(event) => setAttachmentDesc(event.target.value)}
              placeholder="Description"
              style={{ width: "100%" }}
            />
          </div>
          <div style={toolbarStyle}>
            <Button disabled={!canEdit} onClick={handleAddAttachment} variant="secondary">
              Add Attachment
            </Button>
          </div>

          {attachments.length === 0 ? (
            <QuietEmptyText>No attachments added.</QuietEmptyText>
          ) : (
            <div style={listStackStyle}>
              {attachments.map((attachment) => (
                <div key={attachment.id} style={listRowStyle}>
                  <a href={attachment.url} target="_blank" rel="noreferrer" style={linkTitleStyle}>
                    {attachment.url}
                  </a>
                  {attachment.description ? (
                    <div style={listMetaTextStyle}>{attachment.description}</div>
                  ) : null}
                  <div style={listMetaTextStyle}>
                    {attachment.uploaded_by ? `Added by ${attachment.uploaded_by}` : "Added without owner"}
                    {" · "}
                    {attachment.uploaded_at ? new Date(attachment.uploaded_at).toLocaleString() : "Upload date unavailable"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </TabPanel>

      <TabPanel
        tabsId={tabsId}
        tabKey="history"
        label="History"
        activeTab={activeTab}
      >
        <Section title="Notes">
          <div style={composerRowStyle}>
            <Input
              ref={noteInputRef}
              label="Add note"
              value={noteText}
              onChange={(event) => setNoteText(event.target.value)}
              placeholder="Add note"
              style={{ flex: 1, minWidth: 220 }}
            />
            <Button disabled={!canEdit} onClick={handleAddNote} variant="secondary" style={{ marginTop: 18 }}>
              Add
            </Button>
          </div>

          {notes.length === 0 ? (
            <QuietEmptyText>No notes yet.</QuietEmptyText>
          ) : (
            <div style={listStackStyle}>
              {notes.map((note) => (
                <div key={note.id} style={{ ...listRowStyle, ...noteRowStyle }}>
                  {editingNoteId === note.id ? (
                    <div style={{ display: "grid", gap: spacing.sm }}>
                      <Input
                        ariaLabel="Edit note"
                        value={editingNoteText}
                        onChange={(event) => setEditingNoteText(event.target.value)}
                        style={{ width: "100%" }}
                      />
                      <div style={toolbarStyle}>
                        <Button disabled={!canEdit} onClick={() => handleSaveNoteEdit(note.id)} variant="primary">
                          Save
                        </Button>
                        <Button onClick={() => setEditingNoteId(null)} variant="secondary">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={listRowHeaderStyle}>
                        <div style={{ display: "grid", gap: 2 }}>
                          <div style={listTitleStyle}>{note.author || "User"}</div>
                          <div style={listMetaTextStyle}>{new Date(note.created_at).toLocaleString()}</div>
                        </div>
                        <div style={toolbarStyle}>
                          <Button
                            onClick={() => {
                              if (!canEdit) return;
                              setEditingNoteId(note.id);
                              setEditingNoteText(note.body);
                            }}
                            variant="secondary"
                          >
                            Edit
                          </Button>
                          {confirmDeleteNoteId === note.id ? (
                            <DestructiveConfirm
                              actionId="ACTION_NOTE_DELETE"
                              open
                              variantOverride="inline"
                              resourceDisplayName="Note"
                              onCancel={() => setConfirmDeleteNoteId(null)}
                              onConfirm={async () => {
                                if (onDeleteNote) {
                                  await onDeleteNote(note.id);
                                } else {
                                  await deleteActionNote(note.id);
                                }
                                setConfirmDeleteNoteId(null);
                                return async () => {
                                  if (onAddNote) {
                                    await onAddNote(note.body);
                                  } else {
                                    await addActionNote(action.id, {
                                      body: note.body,
                                      author: note.author || action?.owner || null,
                                    });
                                  }
                                };
                              }}
                            />
                          ) : (
                            <Button
                              disabled={!canEdit} onClick={() => setConfirmDeleteNoteId(note.id)}
                              variant="destructive"
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                      <div style={listBodyTextStyle}>{note.body}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Change history">
          {historyEntries.length === 0 ? (
            <QuietEmptyText>No history available.</QuietEmptyText>
          ) : (
            <div style={listStackStyle}>
              {historyEntries.map((entry) => (
                <div key={entry.id} style={listRowStyle}>
                  <div style={historyTypeLabelStyle}>Change</div>
                  <div style={listRowHeaderStyle}>
                    <div style={{ display: "grid", gap: 2 }}>
                      <div style={listTitleStyle}>{entry.field || "Change"}</div>
                      <div style={listMetaTextStyle}>
                        {entry.changed_by ? `${entry.changed_by} · ` : ""}
                        {entry.changed_at ? new Date(entry.changed_at).toLocaleString() : "Timestamp unavailable"}
                      </div>
                    </div>
                  </div>
                  <div style={listBodyTextStyle}>
                    {entry.from_value || "-"} {"->"} {entry.to_value || "-"}
                  </div>
                  {entry.note ? <div style={listMetaTextStyle}>{entry.note}</div> : null}
                </div>
              ))}
            </div>
          )}
        </Section>
      </TabPanel>

      <ConfirmDialog
        open={confirmComplete}
        title="Mark Action Complete"
        message={
          <div>
            <p style={{ margin: 0 }}>Mark {actionTitle} as {statusDraftLabel}?</p>
            <p style={{ margin: "8px 0 0" }}>
              Completion still requires at least one note or attachment, and all child actions must already be completed.
            </p>
          </div>
        }
        confirmLabel={statusSaving ? "Updating..." : "Confirm"}
        confirmVariant="primary"
        cancelVariant="secondary"
        confirmDisabled={statusSaving}
        onCancel={() => setConfirmComplete(false)}
        onConfirm={async () => {
          setConfirmComplete(false);
          await handleStatusSave();
        }}
      />
    </div>
  );
}

function TabPanel({ tabsId, tabKey, label, activeTab, children }) {
  const selected = activeTab === tabKey;
  return (
    <div
      id={`${tabsId}-${tabKey}-panel`}
      role="tabpanel"
      aria-labelledby={`${tabsId}-${tabKey}-tab`}
      hidden={!selected}
      tabIndex={0}
      style={{
        ...tabPanelStyle,
        display: selected ? "grid" : "none",
      }}
      aria-label={label}
    >
      {children}
    </div>
  );
}

function Section({ title, actions, children }) {
  return (
    <section style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <div style={sectionTitleStyle}>{title}</div>
        {actions ? <div style={toolbarStyle}>{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

function SummaryField({ label, value }) {
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div style={summaryFieldStyle}>
      <dt style={summaryLabelStyle}>{label}</dt>
      <dd style={isEmpty ? quietEmptyValueStyle : summaryValueStyle}>
        {isEmpty ? "Not set" : value}
      </dd>
    </div>
  );
}

function RelatedRecord({ label, value, onActivate }) {
  if (!value) {
    return (
      <div style={relatedRecordCardStyle}>
        <div style={summaryLabelStyle}>{label}</div>
        <div style={quietEmptyValueStyle}>Not set</div>
      </div>
    );
  }

  if (!onActivate) {
    return (
      <div style={relatedRecordCardStyle}>
        <div style={summaryLabelStyle}>{label}</div>
        <div style={summaryValueStyle}>{value}</div>
      </div>
    );
  }

  return (
    <button type="button" style={relatedRecordButtonStyle} onClick={onActivate}>
      <span style={summaryLabelStyle}>{label}</span>
      <span style={relatedRecordValueStyle}>{value}</span>
    </button>
  );
}

function ChildActionRow({ child, onOpenAction }) {
  const childTitle = child.title || child.name || `Action #${child.id}`;
  const childReference = `Action #${child.id}`;
  const childStatus = getActionDisplayStatus(child, "Unknown");
  const ownerLabel = formatOptionalText(child.owner || child.assignee);
  const isInteractive = typeof onOpenAction === "function";

  return (
    <button
      type="button"
      style={childActionButtonStyle}
      onClick={() => onOpenAction?.(child.id)}
      disabled={!isInteractive}
      aria-label={`Open child action ${childTitle} (${childReference})`}
    >
      <div style={listRowHeaderStyle}>
        <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
          <div style={listTitleStyle}>{childTitle}</div>
          <div style={listMetaTextStyle}>{childReference}</div>
        </div>
        <div style={badgeRowStyle}>
          <StatusBadge variant="status" value={childStatus} fallbackLabel={childStatus} />
          {child.priority ? (
            <StatusBadge variant="severity" value={child.priority} fallbackLabel={child.priority} label={child.priority} />
          ) : (
            <span style={quietEmptyInlineStyle}>Priority not set</span>
          )}
        </div>
      </div>
      <div style={childMetaGridStyle}>
        <span>Due {child.due_date ? formatDateTime(child.due_date) : "Not set"}</span>
        <span>{`Owner ${ownerLabel}`}</span>
      </div>
    </button>
  );
}

function QuietEmptyText({ children }) {
  return <div style={quietEmptyTextStyle}>{children}</div>;
}

const pageStyle = {
  display: "grid",
  gap: spacing.lg,
};

const errorBannerStyle = {
  border: `1px solid ${colors.dangerBorder}`,
  background: colors.dangerSubtle,
  color: colors.danger,
  borderRadius: radius.md,
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.sm,
};

const tabHeaderStyle = {
  display: "grid",
  gap: spacing.md,
};

const tabListStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.xs,
  borderBottom: `1px solid ${colors.border}`,
  paddingBottom: spacing.xs,
};

const tabButtonStyle = {
  border: "none",
  background: "transparent",
  color: colors.textMuted,
  padding: `${spacing.sm}px ${spacing.md}px`,
  borderRadius: radius.sm,
  boxShadow: "inset 0 -2px 0 transparent",
  fontSize: typography.size.sm,
  fontWeight: typography.weight.medium,
  cursor: "pointer",
};

const tabButtonActiveStyle = {
  color: colors.accent,
  background: colors.surfaceSubtle,
  boxShadow: `inset 0 -2px 0 ${colors.accent}`,
};

const headerActionBarStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const tabPanelStyle = {
  display: "grid",
  gap: spacing.xl,
  outline: "none",
};

const sectionStyle = {
  display: "grid",
  gap: spacing.md,
};

const sectionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const sectionTitleStyle = {
  fontSize: typography.size.base,
  fontWeight: typography.weight.bold,
  color: colors.text,
};

const descriptionTextStyle = {
  margin: 0,
  color: colors.text,
  lineHeight: typography.lineHeight.reading,
  whiteSpace: "pre-wrap",
};

const quietEmptyTextStyle = {
  color: colors.textMuted,
  fontSize: typography.size.sm,
};

const quietEmptyValueStyle = {
  margin: 0,
  color: colors.textMuted,
  fontSize: typography.size.sm,
};

const quietEmptyInlineStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: spacing.sm,
  margin: 0,
};

const summaryFieldStyle = {
  display: "grid",
  gap: 4,
  padding: `${spacing.sm}px 0`,
  borderBottom: `1px solid ${colors.borderSubtle || colors.border}`,
  margin: 0,
};

const summaryLabelStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const summaryValueStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.sm,
  lineHeight: typography.lineHeight.reading,
  wordBreak: "break-word",
};

const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: spacing.md,
};

const createCategoryWrapStyle = {
  display: "grid",
  gap: spacing.sm,
};

const createCategoryHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const createCategoryPanelStyle = {
  display: "grid",
  gap: spacing.sm,
  padding: spacing.md,
  borderRadius: radius.md,
  border: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
};

const inlineHelperTextStyle = {
  color: colors.textMuted,
  fontSize: typography.size.sm,
};

const inlineButtonStyle = {
  alignSelf: "flex-start",
};

const relatedRecordsWrapStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: spacing.sm,
};

const relatedRecordCardStyle = {
  display: "grid",
  gap: spacing.xs,
  padding: spacing.md,
  borderRadius: radius.md,
  border: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
};

const relatedRecordButtonStyle = {
  ...relatedRecordCardStyle,
  textAlign: "left",
  cursor: "pointer",
};

const relatedRecordValueStyle = {
  color: colors.accent,
  fontSize: typography.size.sm,
  fontWeight: typography.weight.medium,
};

const kpiPanelWrapStyle = {
  display: "grid",
  gap: spacing.sm,
};

const subsectionTitleStyle = {
  fontSize: typography.size.sm,
  color: colors.text,
  fontWeight: typography.weight.medium,
};

const workflowBarStyle = {
  display: "flex",
  gap: spacing.sm,
  alignItems: "flex-end",
  flexWrap: "wrap",
};

const relatedActionButtonStyle = {
  display: "grid",
  gap: spacing.xs,
  padding: spacing.md,
  borderRadius: radius.md,
  border: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
  color: colors.text,
  textAlign: "left",
  cursor: "pointer",
};

const relatedActionTitleStyle = {
  fontSize: typography.size.sm,
  fontWeight: typography.weight.bold,
  color: colors.text,
};

const relatedActionMetaStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const listStackStyle = {
  display: "grid",
  gap: spacing.sm,
};

const listRowStyle = {
  display: "grid",
  gap: spacing.sm,
  padding: spacing.md,
  borderRadius: radius.md,
  border: `1px solid ${colors.border}`,
  background: colors.surface,
};

const noteRowStyle = {
  background: colors.surfaceSubtle,
};

const listRowHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const listTitleStyle = {
  fontSize: typography.size.sm,
  fontWeight: typography.weight.bold,
  color: colors.text,
  lineHeight: typography.lineHeight.compact,
};

const listMetaTextStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  lineHeight: typography.lineHeight.form,
};

const listBodyTextStyle = {
  fontSize: typography.size.sm,
  color: colors.text,
  lineHeight: typography.lineHeight.reading,
  whiteSpace: "pre-wrap",
};

const badgeRowStyle = {
  display: "flex",
  gap: spacing.xs,
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const childActionButtonStyle = {
  ...listRowStyle,
  textAlign: "left",
  cursor: "pointer",
};

const childMetaGridStyle = {
  display: "flex",
  gap: spacing.md,
  flexWrap: "wrap",
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const linkRowStyle = {
  display: "block",
  padding: `${spacing.sm}px ${spacing.md}px`,
  borderRadius: radius.md,
  border: `1px solid ${colors.border}`,
  color: colors.accent,
  textDecoration: "none",
  background: colors.surfaceSubtle,
  wordBreak: "break-word",
};

const linkTitleStyle = {
  color: colors.accent,
  textDecoration: "none",
  wordBreak: "break-word",
  fontSize: typography.size.sm,
  fontWeight: typography.weight.medium,
};

const composerRowStyle = {
  display: "flex",
  gap: spacing.sm,
  alignItems: "flex-end",
  flexWrap: "wrap",
};

const historyTypeLabelStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
